import take from 'lodash/take'
import compact from 'lodash/compact'
import Link from 'next/link'
import Faq from 'components/Faq'

import { MarketsNearMeQuery } from './types/marketsNearMe'

type Props = {
  markets: MarketsNearMeQuery['nearbyMarkets']['records'] | null
}

function marketTopTrucks(
  market: MarketsNearMeQuery['nearbyMarkets']['records'][0]
) {
  if (market.topTrucksEnabled && market.marketTopTrucks.length > 0) {
    return take(
      market.marketTopTrucks.map((t) => t.truck),
      3
    )
  }
  return take(market.popularTrucks.records, 3)
}

export default function NearbyFaq({ markets }: Props) {
  if (!markets) {
    return null
  }

  let topTrucks: MarketsNearMeQuery['nearbyMarkets']['records'][0]['popularTrucks']['records'][0][] = []
  let topLots: MarketsNearMeQuery['nearbyMarkets']['records'][0]['lots']['records'][0][] = []
  const marketsWithTrucks = markets.filter(
    (m) =>
      (m.topTrucksEnabled && m.marketTopTrucks.length > 0) ||
      m.popularTrucks.records.length > 0
  )

  const market = marketsWithTrucks[0]

  if (market) {
    topTrucks = marketTopTrucks(market)
    topLots = market.lots.records
  }

  const faqs = [
    topTrucks.length > 0
      ? {
          question: 'What are the best food trucks near me?',
          answer: (
            <>
              Some of the best food trucks near you:{' '}
              {topTrucks.map((t, i) => (
                <Link key={t.id} href={t.scheduleUrl} prefetch={false}>
                  <span>
                    <span className="underline">{t.name}</span>
                    {i + 1 !== topTrucks.length && ', '}
                  </span>
                </Link>
              ))}
            </>
          )
        }
      : null,
    topLots.length > 0
      ? {
          question: 'Where food trucks near me are located?',
          answer: (
            <>
              Food trucks locations near you:{' '}
              {topLots.map((t, i) => (
                <Link key={t.id} href={t.lotPath} prefetch={false}>
                  <span>
                    <span className="underline">{t.name}</span>
                    {i + 1 !== topLots.length && ', '}
                  </span>
                </Link>
              ))}
            </>
          )
        }
      : null,
    {
      question: 'Where are the most food trucks located?',
      answer:
        'Food trucks are typically located where people are. Every region has regulations that dictate where trucks can operate. Regardless of regulations, food trucks try to vend from heavily trafficked pedestrian areas or in business parks. During the pandemic, many trucks vended from communities once or twice per week. Food trucks are all about bringing great food to hungry patrons.'
    },
    {
      question: 'What are the most popular food truck cuisines?',
      answer:
        "Patrons are always excited for great food regardless of whether it's a food truck/a stand/or a restaurant. Sometimes the location of the event dictates what the most popular options are. If you’re at a local curb without tables and chairs, expect to see a lot of handheld dishes that don’t need a fork and knife. If you're at an event with seating people tend to order whatever they’re interested in without regard to ease of consumption."
    },
    {
      question: 'Is there an app to find food trucks?',
      answer: (
        <>
          The best way to find food trucks through an app is BFT. It let&apos;s
          you locate food trucks near you, browse food truck schedule, view food
          truck menus, and order food online. The BFT app can be downloaded for{' '}
          <a
            className="underline"
            href="https://apps.apple.com/us/app/best-food-trucks/id1513823045"
          >
            iOS
          </a>{' '}
          and{' '}
          <a
            className="underline"
            href="https://play.google.com/store/apps/details?id=com.bftcustomers&hl=en"
          >
            Android
          </a>
          .
        </>
      )
    },
    {
      question:
        "What's the best way to bring a food truck to my office or neighborhood?",
      answer: (
        <>
          Unfortunately, the pandemic put a lot of food trucks out of business.
          With the increased demand and the decreased supply, it can be harder
          and harder to get a food truck to commit. The best way to entice a
          food truck to jump into a regular rotation is to offer a minimum
          guarantee ($700-$1000). Food trucks take a lot of risk coming to a new
          location and a minimum guarantee helps to ensure they won’t be left at
          a loss after a lunch or dinner service. You can reach out to trucks
          directly by going to:{' '}
          <Link className="underline" href="/events/form" prefetch={false}>
            https://www.bestfoodtrucks.com/events/form
          </Link>
        </>
      )
    },
    {
      question: 'Can I reserve a food truck for catering?',
      answer: (
        <>
          Food trucks are the perfect fit for catering events. Fresh, made to
          order food out of a full kitchen is the best way to serve your guests.
          The old scoop and serve catering model creates too much waste,
          doesn&apos;t account for consumer dietary restrictions and
          doesn&apos;t match the quality of a freshly cooked meal. To get
          started on a catering request go to our catering form:{' '}
          <Link className="underline" href="/hire-food-truck" prefetch={false}>
            https://www.bestfoodtrucks.com/hire-food-truck
          </Link>
        </>
      )
    },
    {
      question: 'Should I tip at a food truck?',
      answer:
        'Most food truck employees see tips as part of their pay. In fact, most food truck owners will boast about how much an employee could make in tips during the hiring process. If the food truck operator/employee is nice and gets you delicious food in a timely manner, consider tipping them. The employees really appreciate it.'
    }
  ]

  return (
    <Faq
      className="bg-white"
      faqs={compact(faqs)}
      title="Food truck questions"
    />
  )
}
